/**
 * Sprachprinzip Flex4Success v8 (verbindlich):
 * 1. Alltagssprache, keine Anglizismen-Übersetzungen
 * 2. Metaphern aus Andreas' Coaching-Modell (Sanduhr, Radar, Tür/Schlüssel, Standortbestimmung)
 * 3. Psychologisch fundiert, nicht esoterisch
 * 4. Kein Coach-Sprech
 * 5. Werkzeug-Namen in einem Satz erklärbar
 * 6. 'Primärfokustyp' statt 'Fokustyp' im Singular-Kontext
 *
 * Sprint M5.39 (28.07.2026) — FlexCoach-Demo-Seite (Route: /flex-coach).
 * Visuelle Verifikation der 3 Tier-Stufen und Werkzeug-Naming v3
 * (Sanduhr / Radar / Standortbestimmung). Keine Integration in den Mitgliederbereich,
 * Welcome oder Danke — das kommt in Sprint M5.41.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Hourglass, Radar, Compass } from "lucide-react";
import FlexCoachAvatar, { FLEX_COACH_TIERS } from "@/components/FlexCoachAvatar";

const TOOL_TRIO = [
  {
    id: "sanduhr",
    label: "Die Sanduhr",
    principle: "Pattern Interrupt",
    hint: "Wie viel Zeit habe ich noch?",
    Icon: Hourglass,
  },
  {
    id: "radar",
    label: "Das Radar",
    principle: "Mustererkennung",
    hint: "Welcher Primärfokustyp zeigt sich gerade?",
    Icon: Radar,
  },
  {
    id: "standortbestimmung",
    label: "Die Standortbestimmung",
    principle: "Selbstwahrnehmung, primärfokustyp-spezifisch",
    hint: "Wo stehe ich in diesem Gespräch?",
    Icon: Compass,
  },
];

export default function FlexCoachDemo() {
  const tiers = Object.keys(FLEX_COACH_TIERS);

  return (
    <div
      className="min-h-screen bg-slate-50 py-12 sm:py-16 px-4"
      data-testid="flex-coach-demo-page"
      style={{ "--flex-coach-primary": "#4563BB" }}
    >
      <div className="max-w-4xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"
          data-testid="flex-coach-demo-back"
        >
          <ArrowLeft className="w-4 h-4" /> Startseite
        </Link>

        <header className="mt-6 text-center">
          <p
            className="text-xs font-bold uppercase tracking-widest text-[color:var(--flex-coach-primary)]"
            data-testid="flex-coach-demo-eyebrow"
          >
            Sprint M5.39 · FlexCoach-Demo
          </p>
          <h1
            className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900"
            data-testid="flex-coach-demo-headline"
          >
            FlexCoach — dein Begleiter in Flex4Success.
          </h1>
          <p className="mt-3 text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
            Drei Tier-Stufen, drei Grundwerkzeuge. Die finale Illustration
            wird extern geliefert; hier siehst du die Platzhalter und das
            Naming v3 im Zusammenspiel.
          </p>
        </header>

        {/* ============ Avatar-Trio ============ */}
        <section
          className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6"
          data-testid="flex-coach-demo-avatar-grid"
        >
          {tiers.map((tier) => (
            <div
              key={tier}
              className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col items-center"
              data-testid={`flex-coach-demo-card-${tier}`}
            >
              <FlexCoachAvatar tier={tier} size="lg" showCaption />
            </div>
          ))}
        </section>

        {/* ============ Werkzeug-Trio v3 ============ */}
        <section className="mt-16" data-testid="flex-coach-demo-tools">
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 text-center">
            Die drei Grundwerkzeuge
          </h2>
          <p className="mt-2 text-center text-sm text-slate-500 max-w-2xl mx-auto">
            Sanduhr, Radar, Standortbestimmung — jedes Werkzeug in einem Satz erklärbar.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {TOOL_TRIO.map((tool) => {
              const { Icon } = tool;
              return (
                <div
                  key={tool.id}
                  className="rounded-2xl border border-slate-200 bg-white p-5"
                  data-testid={`flex-coach-demo-tool-${tool.id}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-[color:var(--flex-coach-primary)]/10 flex items-center justify-center">
                    <Icon
                      className="w-5 h-5"
                      style={{ color: "var(--flex-coach-primary)" }}
                    />
                  </div>
                  <p className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">
                    {tool.principle}
                  </p>
                  <h3 className="mt-1 text-lg font-extrabold text-slate-900">
                    {tool.label}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {tool.hint}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        <p className="mt-14 text-center text-xs text-slate-400">
          Diese Seite dient als visuelle Verifikation. Integration in den Mitgliederbereich,
          Welcome-Page und Danke-Seite erfolgt in Sprint M5.41.
        </p>
      </div>
    </div>
  );
}
